import Head from "next/head"
import { useState } from "react"
import { t } from "translations"

import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete"
import Grid from "@mui/material/Grid"
import Skeleton from "@mui/material/Skeleton"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import { getFooter, getPage } from "data/contentData/api/content"
import { getMegamenu } from "data/contentData/api/megamenu"
import { IMegamenuCategory } from "data/contentData/interfaces/IMegamenuCategory"
import { IAuthor } from "data/contentData/interfaces/mongodb/IAuthor"
import { IContainerCard } from "data/contentData/interfaces/mongodb/IContainerCard"
import { IPage } from "data/contentData/interfaces/mongodb/IPage"
import { IPublic } from "data/contentData/interfaces/mongodb/IPublic"
import useQueryData from "data/hooks/useQueryData"
import { IDeviceDimensions } from "lib/hooks/deviceDimensions/IDeviceDimensions"
import useDeviceDimensions from "lib/hooks/deviceDimensions/useDeviceDimensions"
import useGtmCustomVariables from "lib/hooks/useGtmCustomVariables"
import { staticPaths } from "lib/routes"

import ArticleMeta from "Components/Article/ArticleMeta"
import Layout from "Components/Layout/Layout"
import PortableText from "Components/PortableText/PortableText"

const headerDeviceDimensions: IDeviceDimensions = {
    sm: {
        height: 78
    },
    md: {
        height: 140
    },
    lg: {
        height: 140
    }
}

const bodyDeviceDimensions: IDeviceDimensions = {
    sm: {
        height: 1800
    },
    md: {
        height: 950
    },
    lg: {
        height: 950
    }
}

export interface ICountry {
    url: string
    name: string
    continent: string
}

export default function TravelVaccinations({
    countries,
    page: initialPage,
    footer,
    megamenu
}: {
    countries: ICountry[]
    page: IPublic
    footer: IContainerCard[]
    megamenu: IMegamenuCategory[]
}) {
    const breakpoint = useDeviceDimensions()
    useGtmCustomVariables()
    const headerSkeletonValues = headerDeviceDimensions[breakpoint]
    const bodySkeletonValues = bodyDeviceDimensions[breakpoint]

    const [selectedCountry, setSelectedCountry] = useState<string | null>(null)

    const handleCountryChange: AutocompleteProps<
        ICountry,
        undefined,
        undefined,
        undefined
    >["onChange"] = (_, value) => {
        setSelectedCountry(value?.url ?? "")
    }

    const {
        data: countryPage,
        error,
        isLoading
    } = useQueryData<IPage | null | IPublic>(
        () =>
            selectedCountry ? `travel-vaccionations-${selectedCountry}` : null,
        () => getPage(selectedCountry as string, true)
    )

    if (error) return null

    const page = (countryPage as IPublic) ?? initialPage

    return (
        <Layout
            footer={footer}
            megamenu={megamenu}>
            <Head>
                <title>{t.travelVaccinations.title}</title>
                <meta
                    name={t.travelVaccinations.title}
                    content={t.travelVaccinations.title}
                    key="desc"
                />
            </Head>
            <StyledWrapper>
                <StyledMain>
                    <Typography variant="h1"> {initialPage.title}</Typography>

                    {isLoading ? (
                        <Skeleton height={headerSkeletonValues.height} />
                    ) : (
                        <Grid
                            item
                            paddingTop={2}
                            paddingBottom={2}>
                            <ArticleMeta page={page as IPublic} />
                        </Grid>
                    )}

                    <Grid
                        item
                        paddingTop={2}
                        paddingBottom={4}>
                        <SelectCountry
                            options={countries}
                            onChange={handleCountryChange}
                        />
                    </Grid>

                    {isLoading ? (
                        <Skeleton height={bodySkeletonValues.height} />
                    ) : (
                        <Grid item>
                            {selectedCountry && (
                                <Typography variant="h2">
                                    {page.title}
                                </Typography>
                            )}

                            {page.sections?.map(currentSection => (
                                <StyledImageSection key={currentSection.url}>
                                    <Typography variant="h3">
                                        {currentSection.name}
                                    </Typography>
                                    <PortableText
                                        value={currentSection.blockContent}
                                    />
                                    {currentSection.sections?.map(
                                        subSection => (
                                            <section key={subSection.url}>
                                                <Typography variant="h4">
                                                    {subSection.name}
                                                </Typography>
                                                <PortableText
                                                    value={
                                                        subSection.blockContent
                                                    }
                                                />
                                            </section>
                                        )
                                    )}
                                </StyledImageSection>
                            ))}

                            {page.sources && (
                                <section>
                                    <Typography
                                        variant="h3"
                                        marginBottom={2}>
                                        {page.sources.name}
                                    </Typography>
                                    {page.sources?.sections?.map(
                                        currentSection => (
                                            <section key={currentSection.name}>
                                                <Typography variant="h4">
                                                    {currentSection.name}
                                                </Typography>
                                                <PortableText
                                                    value={
                                                        currentSection.blockContent
                                                    }
                                                />
                                            </section>
                                        )
                                    )}
                                </section>
                            )}

                            {page.authors && (
                                <AuthorsSection authors={page.authors} />
                            )}
                        </Grid>
                    )}
                </StyledMain>
            </StyledWrapper>
        </Layout>
    )
}

type CountrySelectProps = Omit<
    AutocompleteProps<ICountry, undefined, undefined, undefined>,
    "renderInput" | "getOptionLabel"
>

function SelectCountry(props: CountrySelectProps) {
    return (
        <Autocomplete
            autoHighlight
            getOptionLabel={country => country.name}
            groupBy={country => country.continent}
            noOptionsText={t.travelVaccinations.noCountryFound}
            renderInput={params => (
                <TextField
                    {...params}
                    label={t.travelVaccinations.chooseCountry}
                    variant="outlined"
                />
            )}
            {...props}
        />
    )
}

interface IAuthorsSectionProps {
    authors: IAuthor[]
}

function AuthorsSection({ authors }: IAuthorsSectionProps) {
    const [current, former] = authors.reduce(
        (acc, author) => {
            acc[author.current ? 0 : 1].push(author)

            return acc
        },
        [[], []] as IAuthorsSectionProps["authors"][]
    )

    return (
        <Grid>
            <Typography
                variant="h3"
                marginBottom={2}>
                {t.travelVaccinations.proffesionalStaff}
            </Typography>
            {current.map(author => (
                <Typography key={author.name}>
                    {`${author.name}, ${author.title}`}
                </Typography>
            ))}

            {former.length > 0 && (
                <>
                    <Typography
                        variant="h3"
                        marginBottom={2}>
                        {t.travelVaccinations.previousProffesionalStaff}
                    </Typography>

                    {former.map(author => (
                        <Typography key={author.name}>
                            {`${author.name}, ${author.title}`}
                        </Typography>
                    ))}
                </>
            )}
        </Grid>
    )
}

export async function getStaticProps() {
    const page = await getPage(staticPaths.travelVaccinationsSearch, true)
    const allCountries = await getPage(staticPaths.allCountries, true)

    const countries = allCountries?.children?.reduce(
        (acc, { children, name }) => [
            ...acc,
            ...(children?.map(country => ({ ...country, continent: name })) ??
                [])
        ],
        [] as ICountry[]
    )

    const revalidate = Number(process.env.REVALIDATE_STATIC_PROPS)
    const footer = await getFooter()
    const megamenu = await getMegamenu()

    return {
        props: {
            countries,
            page,
            footer,
            megamenu
        },
        revalidate
    }
}

const StyledWrapper = styled("div")(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    display: "grid",
    gridTemplateColumns: "1fr",
    marginTop: theme.spacing(2)
}))

const StyledMain = styled("main")(({ theme }) => ({
    padding: theme.spacing(4),
    overflowY: "auto"
}))

const StyledImageSection = styled("section")(({ theme }) => ({
    [theme.breakpoints.up("md")]: {
        "& img": {
            maxWidth: 500
        }
    }
}))
